import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import type { OutboxStreamKind } from '#shared/core/event-batch';

/**
 * The one IndexedDB database, `tts`. Stores:
 * - `outbox`: events waiting for signal, keyed by event id, indexed by [kind, streamId].
 * - `kv`: small values (device hints, last sync, the persisted query cache).
 *
 * A module that needs its own store asks for it in docs/changes/ (a version bump here).
 */

export interface OutboxRow {
  /** The event id (uuidv7). Also the server's dedupe key. */
  id: string;
  kind: OutboxStreamKind;
  streamId: string;
  payload: unknown;
  /** Insertion order, monotonic per device. */
  order: number;
  enqueuedAt: number;
  state: 'pending' | 'rejected';
  /** Lowercase reject code from the batch response, when state is rejected. */
  code?: string;
  tries: number;
  /**
   * 'event' (the default, and what every row written before this field existed is): `payload` is
   * one event for the stream's batch endpoint. 'create': `request` is a plain POST (a game or a
   * drill session) that must land BEFORE the stream's events; the stream is held until it does.
   * A create row's id is `create:<kind>:<streamId>`.
   */
  op?: 'event' | 'create';
  request?: { url: string; body: unknown; after?: { kind: OutboxStreamKind; id: string } };
}

interface TtsDB extends DBSchema {
  outbox: {
    key: string;
    value: OutboxRow;
    indexes: { stream: [string, string]; kind: string; state: string };
  };
  kv: { key: string; value: unknown };
}

export const DB_NAME = 'tts';
export const DB_VERSION = 1;

type Handle = IDBPDatabase<TtsDB>;

/**
 * Errors that mean the cached connection is dead, not that the data is. WebKit throws
 * "Connection to Indexed Database server lost" (an UnknownError) after the app was suspended, and
 * InvalidStateError when a transaction starts on a closing connection. A fresh open fixes both.
 */
export function isConnectionLost(e: unknown): boolean {
  const name = (e as { name?: unknown } | null)?.name;
  const msg = String((e as { message?: unknown } | null)?.message ?? '');
  return (
    name === 'InvalidStateError' ||
    name === 'UnknownError' ||
    /connection to indexed database server lost|database connection is closing|connection is closing/i.test(msg)
  );
}

/**
 * One cached connection with a way back. The cache is cleared when the browser terminates the
 * connection, when another tab needs the database (a version change), when an open fails, and by
 * `withDb` when an operation dies with a lost-connection error. `withDb` then reopens and runs the
 * operation once more, so a dead connection costs one retry instead of a degraded session.
 */
export function createDbAccess<D extends { close(): void }>(open: (onLost: () => void) => Promise<D>) {
  let handle: Promise<D> | null = null;
  const get = (): Promise<D> => {
    if (!handle) {
      // A lost connection only clears the cache if it is still the current one.
      const p: Promise<D> = open(() => {
        if (handle === p) handle = null;
      });
      handle = p;
      p.catch(() => {
        if (handle === p) handle = null;
      });
    }
    return handle;
  };
  const reset = () => {
    const old = handle;
    handle = null;
    // Close the old connection if it ever opened, so it cannot hold up the next open.
    old?.then((d) => d.close()).catch(() => {});
  };
  const withDb = async <T>(run: (d: D) => Promise<T>): Promise<T> => {
    try {
      return await run(await get());
    } catch (e) {
      if (!isConnectionLost(e)) throw e;
      reset();
      return run(await get());
    }
  };
  return { get, reset, withDb };
}

const access = createDbAccess<Handle>((onLost) =>
  openDB<TtsDB>(DB_NAME, DB_VERSION, {
    upgrade(d, oldVersion) {
      if (oldVersion < 1) {
        const outbox = d.createObjectStore('outbox', { keyPath: 'id' });
        outbox.createIndex('stream', ['kind', 'streamId']);
        outbox.createIndex('kind', 'kind');
        outbox.createIndex('state', 'state');
        d.createObjectStore('kv');
      }
    },
    blocked() {
      // An old tab holds the DB open. The update prompt reloads everything soon anyway.
    },
    blocking() {
      // A newer build of the app wants to upgrade the database: let go so it can.
      onLost();
    },
    terminated() {
      // The browser killed the connection (storage cleared, WebKit lost its server).
      onLost();
    },
  }),
);

export const db = access.get;

/** Run one database operation, reopening the connection once if it turns out to be dead. */
export const withDb = access.withDb;

/** Forget the cached connection (and close it). The next `db()` opens a fresh one. */
export const resetDb = access.reset;

export function hasIndexedDB(): boolean {
  return typeof indexedDB !== 'undefined';
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  return (await withDb((d) => d.get('kv', key))) as T | undefined;
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await withDb((d) => d.put('kv', value, key));
}

export async function kvDel(key: string): Promise<void> {
  await withDb((d) => d.delete('kv', key));
}

/** Ask the browser not to evict our data (call after joining). */
export async function persistStorage(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
